import { ContactFormData } from '../types';

/**
 * API Service for Backend Integration
 * 
 * Base URL is configured from environment variable REACT_APP_API_URL
 * Default: http://localhost:8000
 */

const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Submit contact form to backend API
 * 
 * @param formData - Contact form data with name, email, message, consent
 * @returns Promise resolving to success message
 * @throws Error if submission fails
 */
export const submitContactForm = async (
  formData: ContactFormData
): Promise<string> => {
  // Filter out empty strings for optional fields
  const payload = {
    ...formData,
    countryCode: formData.countryCode?.trim() || undefined,
    phone: formData.phone?.trim() || undefined,
  };

  const response = await fetch(`${API_URL}/api/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit form: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data.message;
};

export interface CheckoutVerification {
  paid: boolean;
  branch: string;
  service: string;
  serviceName: string;
  email: string | null;
}

/**
 * Start a Stripe Checkout for a branch/service pair. The site charges
 * USD for everyone.
 *
 * @returns The hosted checkout URL to redirect the browser to
 */
export const createCheckout = async (branch: string, service: string): Promise<string> => {
  const response = await fetch(`${API_URL}/api/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ branch, service, currency: 'usd' }),
  });

  if (!response.ok) {
    throw new Error(`Failed to start checkout: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data.url;
};

/**
 * Verify a checkout session after Stripe redirects back, so the
 * booking page can unlock tutor selection.
 */
export const verifyCheckout = async (sessionId: string): Promise<CheckoutVerification> => {
  const response = await fetch(
    `${API_URL}/api/checkout/verify?session_id=${encodeURIComponent(sessionId)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to verify payment: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data;
};

// Export API_URL for configuration reference
export { API_URL };
